/**
 * TabelaDadosContagem - Table displaying imported counting data (Epic 18 - Phase 3)
 *
 * Fetches and displays expected inventory items from API
 * GET /api/contagens/{job_id}/itens-esperados
 */
import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import contagemService, { ItemContagemImportado } from '../../services/contagemService';
import { formatDateBR } from '../../utils/formatters';

interface TabelaDadosContagemProps {
  jobId: number;
  onItensLoaded?: (itens: ItemContagemImportado[]) => void;
}

export default function TabelaDadosContagem({ jobId, onItensLoaded }: TabelaDadosContagemProps) {
  const [itens, setItens] = useState<ItemContagemImportado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;

  const loadItens = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await contagemService.getItensEsperados(jobId, page, pageSize);

      setItens(response.results);
      setCurrentPage(page);
      setTotalPages(Math.ceil(response.count / pageSize));

      // Notify parent
      if (onItensLoaded) {
        onItensLoaded(response.results);
      }
    } catch (err: any) {
      console.error('Erro ao carregar itens:', err);

      if (err.response?.status === 404) {
        setError(
          'Nenhum dado importado encontrado. Clique em "Importar do Excel" para começar.'
        );
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar dados da contagem');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItens();
  }, [jobId]);

  const handleRefresh = () => {
    loadItens(currentPage);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      loadItens(page);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded flex items-start gap-3">
        <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-400">
            Nenhum dado importado
          </h4>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">{error}</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition text-sm font-medium"
        >
          <RefreshCw size={16} />
          Recarregar
        </button>
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <p className="text-lg">Nenhum item importado ainda</p>
        <p className="text-sm mt-2">Clique em "Importar do Excel" para começar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {itens.length} de {totalPages * pageSize} produtos
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
        >
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-lg">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
              <th className="px-3 py-3 text-left font-semibold">Código</th>
              <th className="px-3 py-3 text-left font-semibold">Descrição</th>
              <th className="px-3 py-3 text-left font-semibold">Lote</th>
              <th className="px-3 py-3 text-left font-semibold">Validade</th>
              <th className="px-3 py-3 text-left font-semibold">Faixa Aging</th>
              <th className="px-3 py-3 text-center font-semibold">Qtd Sistema</th>
              <th className="px-3 py-3 text-left font-semibold">GTIN</th>
              <th className="px-3 py-3 text-left font-semibold">Cód Cliente</th>
              <th className="px-3 py-3 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, index) => (
              <tr
                key={item.id}
                onClick={() => setSelectedRow(index)}
                className={`border-b border-gray-200 dark:border-gray-700 transition cursor-pointer ${
                  selectedRow === index
                    ? 'bg-emerald-100 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <td className="px-3 py-2">
                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded font-semibold">
                    {item.codigo_produto}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{item.descricao}</td>
                <td className="px-3 py-2 font-mono text-xs">{item.lote}</td>
                <td className="px-3 py-2">
                  {formatDateBR(item.data_validade)}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${contagemService.getFaixaAgingColor(
                      item.faixa_aging
                    )}`}
                  >
                    {contagemService.getFaixaAgingLabel(item.faixa_aging)}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full font-bold text-xs">
                    {item.quantidade_sistema}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs font-mono">{item.gtin || '-'}</td>
                <td className="px-3 py-2 text-xs">{item.codigo_cliente_produto || '-'}</td>
                <td className="px-3 py-2 text-xs">
                  {item.status_processamento === 'PROCESSADO' ? (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded font-semibold">
                      ✓ OK
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded font-semibold">
                      ✗ {item.status_processamento}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
        <span className="text-gray-500 dark:text-gray-400 italic">
          💡 Clique em uma linha para selecioná-la
        </span>
        <div className="text-gray-700 dark:text-gray-300">
          Total de itens carregados: <span className="font-semibold">{itens.length}</span>
        </div>
      </div>
    </div>
  );
}
